import { TSession } from '@fe-learner-common/api/schemas/sessionsSchema';

type Dates = {
  courseDate?: string | null;
  filterDate?: string | null;
};

type FilterSpec = {
  date?: {
    start: string | null;
    end: string | null;
  };
  duration?: Set<number>;
  format?: Set<string>;
  language?: Set<string>;
  award?: Set<string>;
  location?: Set<string>;
};

export const getLargestDate = ({ courseDate, filterDate }: Dates) => {
  if (!courseDate && !filterDate) return null;
  if (!courseDate) return filterDate;
  if (!filterDate) return courseDate;

  return new Date(courseDate) > new Date(filterDate) ? courseDate : filterDate;
};

export const getSmallestDate = ({ courseDate, filterDate }: Dates) => {
  if (!courseDate && !filterDate) return null;
  if (!courseDate) return filterDate;
  if (!filterDate) return courseDate;

  return new Date(courseDate) < new Date(filterDate) ? courseDate : filterDate;
};

export const getSetOfStrings = (
  newValue?: string | null,
  existing?: Set<string>,
) => {
  if ((!newValue || !newValue.length) && !existing) return null;
  if (!existing) return new Set([newValue]);
  if (!newValue || !newValue.length) return existing;

  return existing.add(newValue);
};

export const getSetOfNumbers = (
  newValue?: number | null,
  existing?: Set<number>,
) => {
  if (!newValue && !existing) return null;
  if (!existing) return new Set([newValue]);
  if (!newValue) return existing;

  return existing.add(newValue);
};

export const getAwards = (session: TSession, awards: Set<string>) => {
  if (!session.certificatesAvailable && !session.diplomasAvailable)
    return awards;

  if (session.certificatesAvailable) {
    awards.add('certificate');
  }
  if (session.diplomasAvailable) {
    awards.add('diploma');
  }

  return awards;
};
const secondsToHours = (seconds: number) => {
  if (!seconds) return 0;
  return Math.floor(seconds / 3600);
};
const calculateDuration = (durations: Set<number>) => {
  if (!durations || !durations.size) {
    return null;
  }

  const max = Math.max(...durations);

  return { min: 0, max: secondsToHours(max) };
};

const mapSetToArray = (property: Set<string> | string[]) => {
  if (property instanceof Set) {
    return Array.from(property);
  }
  return property;
};

const findCourseFilterProperties = ({
  session,
  filter,
}: {
  session: TSession;
  filter: FilterSpec;
}) => {
  const filterKeyValues = Object.entries(filter);

  return filterKeyValues.reduce((acc, [key, value]) => {
    switch (key) {
      case 'award':
        return { award: getAwards(session, value as Set<string>), ...acc };

      case 'date':
        return {
          date: {
            start: getSmallestDate({
              courseDate: session.startDate,
              filterDate: filter?.date?.start,
            }),
            end: getLargestDate({
              courseDate: session.endDate,
              filterDate: filter?.date?.end,
            }),
          },
          ...acc,
        };

      case 'duration':
        return {
          duration: getSetOfNumbers(session.duration, filter.duration),
          ...acc,
        };

      case 'format':
        return {
          format: getSetOfStrings(session.format, filter.format),
          ...acc,
        };

      case 'language':
        return {
          language: getSetOfStrings(session.language, filter.language),
          ...acc,
        };

      case 'location':
        return {
          location: getSetOfStrings(session.location, filter?.location),
          ...acc,
        };
    }
    return acc;
  }, {});
};

export const calculateFilterSpec = ({
  list,
  allowedSet,
}: {
  list?: TSession[];
  allowedSet: Set<string>;
}) => {
  if (!allowedSet || !allowedSet.size || !list?.length) {
    return null;
  }

  const courseFilterProperties = list.reduce(
    (acc, session) => {
      return {
        ...acc,
        ...findCourseFilterProperties({ session, filter: acc }),
      };
    },
    {
      ...(allowedSet.has('dates') && { date: { start: null, end: null } }),
      ...(allowedSet.has('duration') && { duration: new Set<number>() }),
      ...(allowedSet.has('language') && { language: new Set<string>() }),
      ...(allowedSet.has('award') && { award: new Set<string>() }),
      ...(allowedSet.has('format') && { format: new Set<string>() }),
      ...(allowedSet.has('location') && { location: new Set<string>() }),
    },
  );

  return {
    ...(courseFilterProperties.date && {
      date: {
        start: courseFilterProperties?.date?.start,
        end: courseFilterProperties?.date?.end,
      },
    }),
    ...(courseFilterProperties.duration && {
      duration: calculateDuration(courseFilterProperties.duration),
    }),
    ...(courseFilterProperties.language && {
      language: mapSetToArray(courseFilterProperties.language),
    }),
    ...(courseFilterProperties.award && {
      award: mapSetToArray(courseFilterProperties.award),
    }),
    ...(courseFilterProperties.format && {
      format: mapSetToArray(courseFilterProperties.format),
    }),
    ...(courseFilterProperties.location && {
      location: mapSetToArray(courseFilterProperties.location),
    }),
  };
};
